import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import moment from 'moment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { TaskTransferService } from '@nimbus-assistant/services/task-transfer.service';
import { ITask, TaskConversationState } from '@nimbus-assistant/models/task.model';
import { CallTransfer, TransferState } from '@nimbus-assistant/models/call-transfer';
import { TransferType } from '@nimbus-assistant/enums/transfer-type';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { PortalTranslationKeys } from '@nimbus-library/features/localization/models/enums/portal-translation-keys.enum';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { BackgroundTaskFactory } from '@nimbus-library/features/background-task/background-task.factory';
import {
  ConversationOriginType,
  ConsultationDirection,
  ConsultationSessionState,
  ConsultationType,
  consultationConversationStateTranslations,
} from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-footer-transfer',
  templateUrl: './footer-transfer.component.html',
  styleUrls: ['./footer-transfer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SharedUIModule],
})
export class FooterTransferComponent implements OnInit {
  @Input({ required: true }) public task: ITask;
  @Input() public isTransferDisabled = false;

  public readonly ICONS = ICONS;
  public readonly IconWeight = IconWeight;
  public readonly TranslationKeys = TranslationKeys;
  public readonly PortalTranslationKeys = PortalTranslationKeys;
  public readonly TransferState = TransferState;
  public readonly TransferType = TransferType;

  public readonly transferInProgress$ = new BehaviorSubject<boolean>(false);
  public readonly actionsDisabled$ = new BehaviorSubject<boolean>(false);
  public currentMoment$: Observable<moment.Moment>;

  public showTransferBar = false;
  public transferState: TransferState | null = null;
  public transferType: TransferType | null = null;
  public callerName: string | null = null;
  public transferErrorMessage: string | null = null;

  public get isConsultativeTransfer(): boolean {
    const c = this.task?.consultationSession;
    if (!c) {
      return false;
    }
    if ([ConsultationSessionState.Canceled, ConsultationSessionState.Completed].includes(c.currentState)) {
      return false;
    }
    return [
      ConsultationSessionState.Starting,
      ConsultationSessionState.Ongoing,
      ConsultationSessionState.PutOnHold,
      ConsultationSessionState.Conferenced,
    ].includes(c.currentState);
  }

  public get activeConsultation(): ITask['consultationSession'] | null {
    return this.isConsultativeTransfer ? this.task.consultationSession : null;
  }

  public get isConsultantView(): boolean {
    return this.activeConsultation?.direction === ConsultationDirection.Target;
  }

  public get consultantName(): string | null {
    const c = this.activeConsultation;
    if (!c) {
      return null;
    }
    if (this.isConsultantView) {
      const isServiceConsultation = c.consultationType === ConsultationType.Service;
      if (isServiceConsultation && this.isConsultationStarting) {
        return c.sourceService?.displayName ?? c.sourceService?.teamId ?? null;
      }
      return c.isSourceTerminated ? null : (c.sourceAgent?.displayName ?? c.sourceAgent?.id ?? null);
    }
    return c.targetAgent?.displayName ?? c.targetAgent?.id ?? c.targetService?.displayName ?? c.targetService?.teamId ?? null;
  }

  public get mainSessionStateKey(): string | null {
    switch (this.task?.conversationState) {
      case TaskConversationState.PutOnHold:
        return PortalTranslationKeys.ConversationStatesPutOnHold;
      case TaskConversationState.Ongoing:
        return PortalTranslationKeys.ConversationStatesOngoing;
      case TaskConversationState.Transferring:
        return PortalTranslationKeys.ConversationStatesTransferring;
      default:
        return null;
    }
  }

  public get consultationStateKey(): string | null {
    const c = this.activeConsultation;
    if (!c) {
      return null;
    }
    if (c.targetState?.state && c.currentState !== ConsultationSessionState.Ongoing) {
      return consultationConversationStateTranslations[c.targetState.state] ?? null;
    }
    switch (this.effectiveConsultationState) {
      case ConsultationSessionState.Starting:
        return PortalTranslationKeys.ConversationStatesRinging;
      case ConsultationSessionState.Ongoing:
        return PortalTranslationKeys.ConversationStatesOngoing;
      case ConsultationSessionState.Conferenced:
        return PortalTranslationKeys.ConversationStatesOngoing;
      case ConsultationSessionState.PutOnHold:
        return PortalTranslationKeys.ConversationStatesPutOnHold;
      default:
        return null;
    }
  }

  public get consultationSince(): string | null {
    const c = this.activeConsultation;
    return c?.targetState?.since ?? c?.since ?? null;
  }

  public get showConsultationTransferAction(): boolean {
    return this.activeConsultation?.currentState !== ConsultationSessionState.Conferenced;
  }

  public get showConsultationConferenceAction(): boolean {
    return this.activeConsultation?.currentState !== ConsultationSessionState.Conferenced;
  }

  public get isConsultationStarting(): boolean {
    return this.activeConsultation?.currentState === ConsultationSessionState.Starting;
  }

  public get isConferenced(): boolean {
    return this.activeConsultation?.currentState === ConsultationSessionState.Conferenced;
  }

  public get isCallerGone(): boolean {
    return this.isConferenced && !!this.task?.callerHungUpAt;
  }

  public get showConsultationSwapAction(): boolean {
    const c = this.activeConsultation;
    return c?.currentState !== ConsultationSessionState.Conferenced && c?.currentState !== ConsultationSessionState.Starting;
  }

  public get consultationSessionStateClass(): string {
    const c = this.activeConsultation;
    if (!c) {
      return '';
    }
    if (c.targetState?.state && c.currentState !== ConsultationSessionState.Ongoing) {
      return c.targetState.state.toLowerCase();
    }
    return (this.effectiveConsultationState ?? '').toLowerCase();
  }

  public get isDisabled(): boolean {
    return this.transferInProgress$.value || this.isTransferDisabled || this.actionsDisabled$.value || this.isTransferOnUserExitDisabled;
  }

  public get isTransferOnUserExitDisabled(): boolean {
    return this.task?.originType === ConversationOriginType.TransferOnUserExit;
  }

  public get safeTransferTooltip(): TranslationKeys {
    return this.isTransferOnUserExitDisabled
      ? TranslationKeys.ContactActionSafeTransferIsDisabledDueTransferOnUserExit
      : TranslationKeys.AssistantOutboundSafeTransfer;
  }

  public get blindTransferTooltip(): TranslationKeys {
    return this.isTransferOnUserExitDisabled
      ? TranslationKeys.ContactActionBlindTransferIsDisabledDueTransferOnUserExit
      : TranslationKeys.AssistantOutboundBlindTransfer;
  }

  public get consultativeTransferTooltip(): TranslationKeys {
    return this.isTransferOnUserExitDisabled
      ? TranslationKeys.ContactActionConsultationIsDisabledDueTransferOnUserExit
      : TranslationKeys.AssistantOutboundConsultativeTransfer;
  }

  public get transferStateClass(): string {
    switch (this.transferState) {
      case TransferState.Transferring:
        return 'transferring';
      case TransferState.TransferSuccessful:
        return 'transfer-successful';
      case TransferState.TransferFailed:
        return 'transfer-failed';
      case TransferState.InConsultativeTransfer:
        return 'consultative';
      default:
        return '';
    }
  }

  private get effectiveConsultationState(): ConsultationSessionState | null {
    const c = this.activeConsultation;
    if (!c) {
      return null;
    }
    if (c.currentState === ConsultationSessionState.Ongoing) {
      if (this.isConsultantView) {
        return this.task.conversationState === TaskConversationState.PutOnHold
          ? ConsultationSessionState.PutOnHold
          : ConsultationSessionState.Ongoing;
      }
      return this.task.conversationState === TaskConversationState.Ongoing
        ? ConsultationSessionState.PutOnHold
        : ConsultationSessionState.Ongoing;
    }
    return c.currentState ?? null;
  }

  public constructor(
    private readonly taskTransferService: TaskTransferService,
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
    private readonly cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.currentMoment$ = this.backgroundTaskFactory.getServerWatches();

    this.taskTransferService.showTransferBar$.pipe(untilDestroyed(this)).subscribe((show) => {
      this.showTransferBar = show;
      this.cd.markForCheck();
    });

    this.taskTransferService.callTransfer$.pipe(untilDestroyed(this)).subscribe((transfer) => {
      if (!transfer) {
        this.transferInProgress$.next(false);
        this.transferState = null;
        this.transferType = null;
        this.callerName = null;
        this.transferErrorMessage = null;
      } else {
        this.transferState = transfer.state;
        this.transferType = transfer.type ?? null;
        this.callerName = transfer.callerName;
        this.transferErrorMessage = transfer.errorMessage ?? null;
      }
      this.cd.markForCheck();
    });
  }

  // --- Transfer button actions ---

  public onSafeTransfer(): void {
    if (this.isDisabled) {
      return;
    }
    this.processTransfer(TransferType.safeTransfer);
  }

  public onBlindTransfer(): void {
    if (this.isDisabled) {
      return;
    }
    this.processTransfer(TransferType.blindTransfer);
  }

  public onConsultativeTransfer(): void {
    if (this.isDisabled) {
      return;
    }
    this.processTransfer(TransferType.consultativeTransfer);
  }

  // --- Transfer state actions (consultative) ---

  public onTransferConsultation(): void {
    this.executeAction(this.taskTransferService.transferConsultationCall());
  }

  public onTerminateCall(): void {
    this.executeAction(this.taskTransferService.terminateCall());
  }

  public onTerminateConsultation(): void {
    this.executeAction(this.taskTransferService.terminateConsultationCall());
  }

  public onSwap(): void {
    this.executeAction(this.taskTransferService.swapConsultationCall());
  }

  public onConference(): void {
    this.executeAction(this.taskTransferService.mergeConsultationCall());
  }

  public onHangupAndRetrieve(): void {
    this.executeAction(this.taskTransferService.terminateConsultationCallAndResume());
  }

  private executeAction(action$: Observable<void>): void {
    this.actionsDisabled$.next(true);
    action$
      .pipe(
        finalize(() => this.actionsDisabled$.next(false)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private processTransfer(transferType: TransferType): void {
    if (!this.task) {
      return;
    }
    this.transferInProgress$.next(true);
    const serviceName =
      transferType === TransferType.blindTransfer
        ? (this.task.initialServiceName ?? this.task.teamInfo?.upn)
        : this.task.initialServiceName;
    this.taskTransferService.processTransfer(
      new CallTransfer(
        this.task.unifiedConversationId,
        this.task.conversationId,
        this.task.modality,
        transferType,
        this.task.teamId,
        serviceName,
        this.task.caller?.displayName,
        !!this.task.teamInfo?.servicePstnNumber,
      ),
    );
  }
}
